// /mobile/dashboard — モバイル Dashboard (Daily Companion 化, 2026-09-16)
//
// 設計判断:
// - Mobile = PC Dashboard の Companion ではなく「毎日 1 回開いて今日やることを知る」目的
// - 2 モジュールだけ:KPI 2×2(4 块) + 🤖 今日のおすすめ(単条 CTA)
// - Quick Access 6 宫格 / 折线图 / Mail Card / 最近更新 Card 全削除
// - PC DashboardPage.tsx は触らない(mobile-companion 铁律)
// - sessionStorage cache key は mobile.* prefix、PC と独立(衝突防止)
//
// データソース(PC と同 API、同 30 min TTL、同一 fallback):
// - mail:   mailV2Api.sharedMailMeta / listMessages
// - ga4:    ga4Api.getReport("30d")
// - gbp:    googleBusinessApi.getDashboard
// - ig:     instagramApi.dashboard(connection check 后)
// - wp:     blogApi.listPosts(publish + draft, per_page=1)
// - ai:     dashboardApi.generateAiInsight(5 模块集約 + samples)
//
// AI キャッシュ cross-day 自動 refresh:sessionStorage の savedAt が
// 「今日」と違う日付なら cache 無視して force fetch。
// これは mobile のみ、PC にはない mobile 独自挙動(「今日」感)。

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Eye,
  Target,
  PenSquare,
  Mail,
  RefreshCw,
} from "lucide-react";
import { MobileHeader } from "../components/MobileHeader";
import { MobileStatCard } from "../components/MobileStatCard";
import { MobileAiRecommendationCard } from "../components/MobileAiRecommendationCard";
import { MobileQuickAccessCard } from "../components/MobileQuickAccessCard";
import { useMailAccounts } from "../../modules/mail-v2/hooks/useMailAccounts";
import { useInstagramConnection } from "../../modules/instagram/hooks/useInstagram";
import { refreshMailIfStale } from "../../modules/mail-v2/services/mailRefreshManager";
import { mailV2Api, type MailMessageV2DTO, type MailSharedMetaDTO } from "../../api/mail-v2";
import { ga4Api, type Ga4Report } from "../../api/ga4";
import { googleBusinessApi, type GbpDashboard } from "../../api/googleBusiness";
import { blogApi } from "../../api/blog";
import { instagramApi } from "../../api/instagram";
import {
  dashboardApi,
  type DashboardAiInsight,
  type MailSample,
} from "../../api/dashboard";
import { ApiError } from "../../api/client";
import type { DashboardResponse as InstagramDashboardResponse } from "../../modules/instagram/types";

// ============================================================
// 状态 4 值 + 30 min sessionStorage cache
// ============================================================

type LoadState<T> =
  | { kind: "loading" }
  | { kind: "ready"; data: T; fetchedAt: Date }
  | { kind: "error"; code: string; message: string };

const PLACEHOLDER = "—";
const MAIL_V2_REAL = import.meta.env.VITE_MAIL_V2_REAL === "true";
// 2026-09-16:Dashboard 数据 + AI Recommendation 同 10 min cache。
//   「秒开」体验 + AI 推荐 stale 控制在 10 min 以内。
const CACHE_TTL_MS = 10 * 60 * 1000;

type TimedCache<T> = { savedAt: number; value: T };

function readTimedCache<T>(key: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TimedCache<T>;
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > CACHE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch {
    return null;
  }
}

function writeTimedCache<T>(key: string, value: T): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), value }));
  } catch {
    // sessionStorage unavailable in private/restricted browser contexts.
  }
}

// Mobile 用 cache key(PC の dashboard.* と独立)
const K = {
  sharedMailMeta: "mobile.dashboard.sharedMailMeta.7",
  mailSamples: "mobile.dashboard.mail.samples",
  ga4: "mobile.dashboard.ga4.30",
  gbp: "mobile.dashboard.gbp.30",
  ig: "mobile.dashboard.ig.30",
  wp: "mobile.dashboard.wp.content",
  aiInsight: "mobile.dashboard.aiInsight.v1",
} as const;

// ============================================================
// Mobile Dashboard Page
// ============================================================

export const MobileDashboardPage = () => {
  // ─── Mail accounts (for shared filter) ───
  const mailAccounts = useMailAccounts();
  const sharedMailAccounts = useMemo(
    () => mailAccounts.accounts.filter((account) => account.accountType === "shared"),
    [mailAccounts.accounts],
  );
  const sharedMailIds = sharedMailAccounts.map((account) => account.id).join("|");

  // ─── Instagram connection check (gate IG row) ───
  const igConnection = useInstagramConnection();
  const isIgConnected = igConnection.data?.status === "connected";

  // ─── 6 source states ───
  const [sharedMail, setSharedMail] = useState<LoadState<MailSharedMetaDTO>>({ kind: "loading" });
  const [mailSamples, setMailSamples] = useState<LoadState<MailSample[]>>({ kind: "loading" });
  const [ga4, setGa4] = useState<LoadState<Ga4Report>>({ kind: "loading" });
  const [gbp, setGbp] = useState<LoadState<GbpDashboard | null>>({ kind: "loading" });
  const [wp, setWp] = useState<LoadState<{ publish: number; draft: number }>>({ kind: "loading" });
  const [ig, setIg] = useState<LoadState<InstagramDashboardResponse | null>>({ kind: "loading" });

  // ─── Global refresh (右上角 refresh button) ───
  // - 6 source useEffect は全部 refreshNonce を deps に入れる
  // - refresh ボタン click → cache 全 clear + nonce bump → 6 effect が全て再 fetch
  // - refreshing 状態は 1.2s 待ってから解除(spin 动画が短すぎないように)
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(() => {
    if (refreshing) return;
    try {
      Object.values(K).forEach((key) => window.sessionStorage.removeItem(key));
    } catch {
      // sessionStorage unavailable
    }
    setRefreshing(true);
    setRefreshNonce((n) => n + 1);
    window.setTimeout(() => setRefreshing(false), 1200);
  }, [refreshing]);

  // ─── Effect 1: shared mail meta ───
  useEffect(() => {
    if (!MAIL_V2_REAL) return;
    if (mailAccounts.loading) return;
    if (sharedMailAccounts.length === 0) {
      const empty: MailSharedMetaDTO = {
        period: { days: 7, start: "", end: "" },
        shared_account_count: 0,
        shared_mail_count: 0,
        unread_count: 0,
        unhandled_count: 0,
        handling_count: 0,
        completed_count: 0,
        daily: [],
        accounts: [],
        signals: [],
      };
      setSharedMail({ kind: "ready", data: empty, fetchedAt: new Date() });
      return;
    }
    let alive = true;
    const cached = readTimedCache<MailSharedMetaDTO>(K.sharedMailMeta);
    if (cached) {
      setSharedMail({ kind: "ready", data: cached, fetchedAt: new Date() });
    } else {
      setSharedMail({ kind: "loading" });
    }
    (async () => {
      try {
        await Promise.all(
          sharedMailAccounts.map((account) =>
            refreshMailIfStale(account.id, { force: true, source: "dashboard" }),
          ),
        );
        const meta = await mailV2Api.sharedMailMeta({ days: 7 });
        if (!alive) return;
        writeTimedCache(K.sharedMailMeta, meta);
        setSharedMail({ kind: "ready", data: meta, fetchedAt: new Date() });
      } catch (err) {
        if (!alive) return;
        if (!cached) {
          setSharedMail({
            kind: "error",
            code: "MAIL_SHARED_META_FETCH_FAILED",
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [mailAccounts.loading, sharedMailIds, sharedMailAccounts, refreshNonce]);

  // ─── Effect 2: mail samples (top 30 inbound for AI) ───
  useEffect(() => {
    if (mailAccounts.loading) return;
    if (sharedMailAccounts.length === 0) {
      setMailSamples({ kind: "ready", data: [], fetchedAt: new Date() });
      return;
    }
    let alive = true;
    const cached = readTimedCache<MailSample[]>(K.mailSamples);
    if (cached) setMailSamples({ kind: "ready", data: cached, fetchedAt: new Date() });
    (async () => {
      try {
        if (!cached) setMailSamples({ kind: "loading" });
        const lists = await Promise.all(
          sharedMailAccounts.map((account) =>
            mailV2Api
              .listMessages({
                mail_account_id: account.id,
                mailbox: "INBOX",
                page: 1,
                page_size: 30,
              })
              .then((res) => ({ account, items: res.items ?? [] }))
              .catch(() => ({ account, items: [] as MailMessageV2DTO[] })),
          ),
        );
        const accountById = new Map(sharedMailAccounts.map((a) => [a.id, a]));
        const merged: MailSample[] = [];
        for (const { account, items } of lists) {
          for (const m of items) {
            if (m.direction !== "inbound") continue;
            const preview = m.preview ? m.preview.slice(0, 80) : null;
            merged.push({
              from_name: m.from_name ?? null,
              from_email: m.from_email,
              subject: m.subject ?? null,
              preview,
              received_at: m.received_at ?? m.created_at ?? null,
              status: m.status,
              is_read: m.is_read,
              account_email: accountById.get(m.mail_account_id)?.email ?? null,
            });
          }
        }
        merged.sort((a, b) => {
          const ta = a.received_at ? Date.parse(a.received_at) : 0;
          const tb = b.received_at ? Date.parse(b.received_at) : 0;
          return tb - ta;
        });
        const top = merged.slice(0, 30);
        if (!alive) return;
        writeTimedCache(K.mailSamples, top);
        setMailSamples({ kind: "ready", data: top, fetchedAt: new Date() });
      } catch (err) {
        if (!alive) return;
        if (cached) return;
        setMailSamples({
          kind: "error",
          code: "MAIL_SAMPLES_FETCH_FAILED",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [mailAccounts.loading, sharedMailIds, sharedMailAccounts, refreshNonce]);

  // ─── Effect 3: GA4 report (30d) ───
  useEffect(() => {
    let alive = true;
    const cached = readTimedCache<Ga4Report>(K.ga4);
    if (cached) setGa4({ kind: "ready", data: cached, fetchedAt: new Date() });
    (async () => {
      try {
        if (!cached) setGa4({ kind: "loading" });
        const r = await ga4Api.getReport("30d");
        if (!alive) return;
        writeTimedCache(K.ga4, r);
        setGa4({ kind: "ready", data: r, fetchedAt: new Date() });
      } catch (e) {
        if (!alive) return;
        if (cached) return;
        if (e instanceof ApiError && e.status === 404) {
          setGa4({ kind: "error", code: "AUTH_GA4_NOT_CONFIGURED", message: "未連携" });
        } else {
          const msg = e instanceof Error ? e.message : String(e);
          setGa4({ kind: "error", code: "UNKNOWN", message: msg });
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshNonce]);

  // ─── Effect 4: GBP dashboard ───
  useEffect(() => {
    let alive = true;
    const cached = readTimedCache<GbpDashboard | null>(K.gbp);
    if (cached !== null) setGbp({ kind: "ready", data: cached, fetchedAt: new Date() });
    (async () => {
      try {
        if (cached === null) setGbp({ kind: "loading" });
        const dashboard = await googleBusinessApi.getDashboard(undefined, false);
        if (!alive) return;
        writeTimedCache(K.gbp, dashboard);
        setGbp({ kind: "ready", data: dashboard, fetchedAt: new Date() });
      } catch (err) {
        if (!alive) return;
        if (cached !== null) return;
        setGbp({
          kind: "error",
          code: "GBP_FETCH_FAILED",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshNonce]);

  // ─── Effect 5: WP content counts ───
  useEffect(() => {
    let alive = true;
    const cached = readTimedCache<{ publish: number; draft: number }>(K.wp);
    if (cached) setWp({ kind: "ready", data: cached, fetchedAt: new Date() });
    (async () => {
      try {
        if (!cached) setWp({ kind: "loading" });
        const [published, drafts] = await Promise.all([
          blogApi.listPosts({ status: "publish", page: 1, per_page: 1 }),
          blogApi.listPosts({ status: "draft", page: 1, per_page: 1 }),
        ]);
        if (!alive) return;
        const data = { publish: published.total ?? 0, draft: drafts.total ?? 0 };
        writeTimedCache(K.wp, data);
        setWp({ kind: "ready", data, fetchedAt: new Date() });
      } catch (err) {
        if (!alive) return;
        if (cached) return;
        setWp({
          kind: "error",
          code: "WP_CONTENT_FETCH_FAILED",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [refreshNonce]);

  // ─── Effect 6: Instagram dashboard (gated on connection) ───
  useEffect(() => {
    if (igConnection.loading) return;
    if (!isIgConnected) {
      setIg({ kind: "ready", data: null, fetchedAt: new Date() });
      return;
    }
    let alive = true;
    const cached = readTimedCache<InstagramDashboardResponse>(K.ig);
    if (cached) setIg({ kind: "ready", data: cached, fetchedAt: new Date() });
    (async () => {
      try {
        if (!cached) setIg({ kind: "loading" });
        const data = await instagramApi.dashboard();
        if (!alive) return;
        writeTimedCache(K.ig, data);
        setIg({ kind: "ready", data, fetchedAt: new Date() });
      } catch (err) {
        if (!alive) return;
        if (cached) return;
        const code =
          err instanceof ApiError ? err.code ?? "IG_DASHBOARD_FAILED" : "IG_DASHBOARD_FAILED";
        setIg({
          kind: "error",
          code,
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [igConnection.loading, isIgConnected, refreshNonce]);

  // ─── Effect 7: AI insight ───
  // - 同日 cache hit → 即 ready
  // - 跨日 cache hit / 無 cache → 1.2s 待って force fetch(他 source の settle を待つ)
  // - PC と同 useRef mirror pattern(source state 変化では再 fetch しない)
  // - aiFetchedAt は成功 fetch 時にのみ更新 → refresh 中も古い timestamp を保持
  //   (Card は refreshing 状態で skeleton body + 旧 timestamp を表示)
  const [aiInsight, setAiInsight] = useState<LoadState<DashboardAiInsight>>({ kind: "loading" });
  const [aiFetchedAt, setAiFetchedAt] = useState<Date | null>(null);
  const [aiRefreshing, setAiRefreshing] = useState(false);

  const sharedMailRef = useRef(sharedMail);
  const mailSamplesRef = useRef(mailSamples);
  const ga4Ref = useRef(ga4);
  const gbpRef = useRef(gbp);
  const igRef = useRef(ig);
  const wpRef = useRef(wp);
  useEffect(() => { sharedMailRef.current = sharedMail; }, [sharedMail]);
  useEffect(() => { mailSamplesRef.current = mailSamples; }, [mailSamples]);
  useEffect(() => { ga4Ref.current = ga4; }, [ga4]);
  useEffect(() => { gbpRef.current = gbp; }, [gbp]);
  useEffect(() => { igRef.current = ig; }, [ig]);
  useEffect(() => { wpRef.current = wp; }, [wp]);

  const fetchAiInsight = useCallback(async (isManualRefresh: boolean) => {
    if (isManualRefresh) {
      setAiRefreshing(true);
      // 旧 recommendation 表示は維持しつつ skeleton body に切替
      setAiInsight((prev) => (prev.kind === "ready" ? prev : { kind: "loading" }));
    } else {
      setAiInsight({ kind: "loading" });
    }
    try {
      const ga4R = ga4Ref.current;
      const gbpR = gbpRef.current;
      const mailR = sharedMailRef.current;
      const igR = igRef.current;
      const wpR = wpRef.current;
      const samplesR = mailSamplesRef.current;

      const ga4Data = ga4R.kind === "ready" ? ga4R.data : null;
      const gbpData = gbpR.kind === "ready" ? gbpR.data : null;
      const mailData = mailR.kind === "ready" ? mailR.data : null;
      const igData = igR.kind === "ready" ? igR.data : null;
      const wpData = wpR.kind === "ready" ? wpR.data : null;
      const samples = samplesR.kind === "ready" ? samplesR.data : [];

      const insight = await dashboardApi.generateAiInsight({
        ga4: ga4Data
          ? {
              kpis: {
                screenPageViews: ga4Data.kpis.screenPageViews,
                totalUsers: ga4Data.kpis.totalUsers,
                conversions: ga4Data.kpis.conversions,
              },
              pages: (ga4Data.pages ?? []).slice(0, 5).map((p) => ({
                path: p.path,
                pv: p.pv,
              })),
            }
          : {},
        gbp: gbpData
          ? {
              metrics30d: {
                searches: gbpData.metrics30d.searches,
                websiteClicks: gbpData.metrics30d.websiteClicks,
                calls: gbpData.metrics30d.calls,
                directions: gbpData.metrics30d.directions,
              },
            }
          : {},
        mail: mailData
          ? {
              shared_mail_count: mailData.shared_mail_count,
              unread_count: mailData.unread_count,
              unhandled_count: mailData.unhandled_count,
              recent_messages: samples,
            }
          : samples.length > 0
            ? { recent_messages: samples }
            : {},
        instagram: igData
          ? {
              pending_dm: igData.metrics.pending_dm,
              pending_comments: igData.metrics.pending_comments,
              followers: igData.metrics.followers,
            }
          : {},
        wordpress: wpData ? { publish: wpData.publish, draft: wpData.draft } : {},
      });

      // cache に savedAt 込みで保存(cross-day 判定用)
      const now = new Date();
      writeTimedCache(K.aiInsight, { insight, savedAt: now.getTime() });
      setAiInsight({ kind: "ready", data: insight, fetchedAt: now });
      setAiFetchedAt(now);
    } catch (err) {
      console.error("[mobile-dashboard] AI insight fetch failed:", err);
      setAiInsight({
        kind: "error",
        code: "DASHBOARD_AI_INSIGHT_FAILED",
        message: err instanceof Error ? err.message : String(err),
      });
    } finally {
      if (isManualRefresh) {
        setAiRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    const cached = readTimedCache<{ insight: DashboardAiInsight; savedAt: number }>(K.aiInsight);
    if (cached) {
      const cachedDate = new Date(cached.savedAt);
      const today = new Date();
      const sameDay = cachedDate.toDateString() === today.toDateString();
      if (sameDay) {
        setAiInsight({ kind: "ready", data: cached.insight, fetchedAt: cachedDate });
        setAiFetchedAt(cachedDate);
        return;
      }
    }
    // 无 cache / 跨日 → 1.2s 待って force fetch(各 source settle を待つ)
    const timer = window.setTimeout(() => {
      void fetchAiInsight(false);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [fetchAiInsight]);

  // ─── Manual refresh trigger → AI 再 fetch ───
  // handleRefresh で bump された refreshNonce を watch して再 fetch。
  // 初回 mount 時は refreshNonce=0 なので trigger しない。
  useEffect(() => {
    if (refreshNonce === 0) return;
    void fetchAiInsight(true);
  }, [refreshNonce, fetchAiInsight]);

  // ============================================================
  // KPI 4 块:漏斗逻辑(运营 → 流量 → 转化 → 内容)
  // ============================================================

  const unhandledMail = sharedMail.kind === "ready" ? sharedMail.data.unhandled_count : null;
  const ga4Pv = ga4.kind === "ready" ? ga4.data.kpis.screenPageViews : null;
  const ga4Cv = ga4.kind === "ready" ? ga4.data.kpis.conversions : null;
  const gbpSearches = gbp.kind === "ready" && gbp.data ? gbp.data.metrics30d.searches : null;
  const reachTotal =
    (typeof ga4Pv === "number" ? ga4Pv : 0) + (typeof gbpSearches === "number" ? gbpSearches : 0);
  const reachReady = typeof ga4Pv === "number" && typeof gbpSearches === "number";
  const wpPublish = wp.kind === "ready" ? wp.data.publish : null;

  const aiInsightReady = aiInsight.kind === "ready" ? aiInsight.data : null;

  return (
    <>
      <MobileHeader
        title="ダッシュボード"
        rightAction={
          <button
            type="button"
            className="mobile-appbar-icon-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="強制更新"
            title={refreshing ? "更新中..." : "強制更新"}
          >
            <RefreshCw
              size={18}
              strokeWidth={1.8}
              className={refreshing ? "spin" : ""}
            />
          </button>
        }
      />
      <main className="mobile-content mobile-dashboard-content">
        {/* Row 1: KPI 2×2 漏斗(未対応X-ル / Reach / CV / 公開) */}
        <div className="mobile-stat-grid">
          <MobileStatCard
            label="未対応メール"
            value={unhandledMail === null ? PLACEHOLDER : unhandledMail.toLocaleString()}
            unit={unhandledMail === null ? "" : "件"}
            icon={<Mail size={18} strokeWidth={2} />}
            iconColor="#ef4444"
            iconBg="#fee2e2"
          />
          <MobileStatCard
            label="Reach"
            value={reachReady ? reachTotal.toLocaleString() : PLACEHOLDER}
            unit=""
            icon={<Eye size={18} strokeWidth={2} />}
            iconColor="#2563eb"
            iconBg="#eff6ff"
          />
          <MobileStatCard
            label="CV"
            value={ga4Cv === null ? PLACEHOLDER : ga4Cv.toLocaleString()}
            unit=""
            icon={<Target size={18} strokeWidth={2} />}
            iconColor="#16a34a"
            iconBg="#dcfce7"
          />
          <MobileStatCard
            label="公開コンテンツ"
            value={wpPublish === null ? PLACEHOLDER : wpPublish.toLocaleString()}
            unit={wpPublish === null ? "" : "件"}
            icon={<PenSquare size={18} strokeWidth={2} />}
            iconColor="#7c3aed"
            iconBg="#f5f3ff"
          />
        </div>

        {/* Row 2: 🤖 今日のおすすめ(単条 CTA + skeleton on refresh) */}
        <MobileAiRecommendationCard
          loading={aiInsight.kind === "loading"}
          refreshing={aiRefreshing}
          insight={aiInsightReady}
          fetchedAt={aiFetchedAt}
        />

        {/* Row 3: Quick Access(GB + Instagram, IG 未接続は非表示) */}
        <MobileQuickAccessCard isInstagramConnected={isIgConnected} />
      </main>
    </>
  );
};
